import { useCallback, useMemo } from 'react';

import { useNavigate } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useIsExternalApplicationFlowEnabled } from '@xing-com/crate-jobs-hooks';

import { useGetApplyUrlWithTrackingParams } from './use-get-apply-url-with-tracking-params';
import type { ApplyTrackingProps } from './use-track-apply-intention';
import { useTrackApplyIntention } from './use-track-apply-intention';

type Props = {
  applyUrl?: string;
  isPrioritizedJob?: boolean;
  jobId: string;
  slug?: string;
  trackingProps?: ApplyTrackingProps;
};

type UrlApplyProps = {
  onClick: () => void;
};

export const useApplyButtonUrlApplyProps = ({
  slug,
  applyUrl,
  jobId,
  isPrioritizedJob = false,
  trackingProps,
}: Props): UrlApplyProps => {
  const navigate = useNavigate();
  const trackApplyIntention = useTrackApplyIntention({
    applicationType: 'UrlApplication',
    jobId,
  });
  const { isLoggedOut } = useLoginState();
  const { isExternalApplicationFlowEnabled } =
    useIsExternalApplicationFlowEnabled(isPrioritizedJob);

  const applyUrlWithTrackingParams = useGetApplyUrlWithTrackingParams(applyUrl);

  const showExternalApplyFlow = useCallback(() => {
    navigate(`/jobs/external-apply/${slug}`);
  }, [slug]);

  const onClick = useCallback(() => {
    trackApplyIntention(trackingProps);
    if (isLoggedOut && isExternalApplicationFlowEnabled) {
      showExternalApplyFlow();
    } else {
      window.open(applyUrlWithTrackingParams, '_blank', 'noopener');
    }
  }, [
    applyUrlWithTrackingParams,
    isExternalApplicationFlowEnabled,
    isLoggedOut,
    showExternalApplyFlow,
    trackApplyIntention,
    trackingProps,
  ]);

  const urlProps = useMemo(
    () => ({
      to: undefined,
      onClick,
    }),
    [onClick]
  );

  return urlProps;
};
